import {
  EntityType,
  FileType,
  ProgrammingReportAttributes,
  ProgramSummaryExportableType,
  ReportEntry,
  ScreenshotOpts,
} from 'spw-redux/modules/reports/types';
import {
  MACRO_PROGRAM_SUMMARY_CARD_WIDTH,
  MACRO_PROGRAM_SUMMARY_CARDS,
  PROGRAM_SUMMARY_CARD_ACTIONS,
  PROGRAM_SUMMARY_CARD_WIDTH,
  PROGRAM_SUMMARY_CARDS,
  PROJECT_EXPORTABLE,
} from 'spw-programming-projects/constants';
import { ProgramSummaryExportableTypes } from 'spw-redux/modules/reports/constants';
import {
  AMENITY_SPACES_CATEGORY,
  CUSTOM_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import { categoryCardScreenshotOptions } from 'spw-projects/reports/utils';
import { isMacro } from 'spw-components/worksheet/utils';
import { Report, ReportRequest } from '../Report';
import { PlainWorksheet } from '../Worksheet';
import { PlainProgramScenario } from '../Scenario';

export default class ProgramSummaryReport implements Report {
  exportableType: ProgramSummaryExportableType;

  projectId: number;

  entityId: number;

  entityType: EntityType;

  isOpen: boolean;

  showRsf: boolean;

  shouldShowClientFacingNames: boolean;

  format: FileType;

  worksheet: PlainWorksheet;

  scenario: PlainProgramScenario;

  constructor(attributes: ProgrammingReportAttributes) {
    this.exportableType = attributes.exportableType;
    this.projectId = attributes.project.id;
    this.entityType = attributes.entityType;
    this.entityId = attributes.entityId;
    this.scenario = attributes.scenario;
    this.worksheet = attributes.worksheet;
    this.isOpen = attributes.isOpen;
    this.showRsf = attributes.showRsf;
    this.shouldShowClientFacingNames = attributes.shouldShowClientFacingNames;
    this.format = attributes.format;
  }

  private getProgramSummaryCards = () => {
    let programSummaryCards: string[] = Object.values(PROGRAM_SUMMARY_CARDS);

    if (isMacro(this.scenario)) {
      if (this.scenario.isShared) {
        programSummaryCards = programSummaryCards.filter(
          (programSummaryCard: PROGRAM_SUMMARY_CARDS) => {
            return ![
              PROGRAM_SUMMARY_CARDS.TOTAL_PROGRAM_ALLOCATION,
              PROGRAM_SUMMARY_CARDS.SPACE_TYPE_ALLOCATION,
              PROGRAM_SUMMARY_CARDS.MEETING_SEAT_RATIO,
            ].includes(programSummaryCard);
          }
        );

        programSummaryCards = programSummaryCards.concat([
          MACRO_PROGRAM_SUMMARY_CARDS.SPACE_TYPE_ALLOCATION_SUBTOTAL_PROGRAM_USF,
          MACRO_PROGRAM_SUMMARY_CARDS.SPACE_TYPE_ALLOCATION_IW_USF,
          MACRO_PROGRAM_SUMMARY_CARDS.SEAT_TYPE_MIX_IW,
        ]);
      }
    }

    return programSummaryCards;
  };

  private getIndividualWorkspacesCardWidth = (cardType: string) => {
    let width = PROGRAM_SUMMARY_CARD_WIDTH[cardType];
    if (isMacro(this.scenario) && cardType === 'usfMix') {
      width = MACRO_PROGRAM_SUMMARY_CARD_WIDTH.individualWorkspacesUsfMix;
    } else if (isMacro(this.scenario)) {
      width = MACRO_PROGRAM_SUMMARY_CARD_WIDTH[cardType];
    }
    return width;
  };

  private getCategoryReportEntries = (
    reportEntry: Partial<ReportEntry>,
    category: string,
    cards: string[]
  ): ReportEntry[] => {
    const screenshotsOpts = cards.map((cardType: string) => {
      let width;
      if (category === INDIVIDUAL_WORKSPACES_CATEGORY) {
        width = this.getIndividualWorkspacesCardWidth(cardType);
      } else if (isMacro(this.scenario)) {
        width = MACRO_PROGRAM_SUMMARY_CARD_WIDTH[cardType];
      } else {
        width = PROGRAM_SUMMARY_CARD_WIDTH[cardType];
      }

      return categoryCardScreenshotOptions(
        this.format.toUpperCase(),
        category,
        cardType,
        width
      );
    });

    return (this.scenario.isDetailed &&
      category === INDIVIDUAL_WORKSPACES_CATEGORY) ||
      (category === SUPPORT_SPACES_CATEGORY && this.worksheet.hasStorageRows)
      ? [true, false].map(
          (variant) =>
            ({
              ...reportEntry,
              variants: {
                ...reportEntry.variants,
                isOpen: variant,
              },
              screenshotsOpts,
            } as ReportEntry)
        )
      : [{ ...reportEntry, screenshotsOpts } as ReportEntry];
  };

  getReportRequest() {
    const reportEntry = {
      entityId: this.entityId,
      entityType: this.entityType,
      exportable: PROJECT_EXPORTABLE.PROGRAM_SUMMARY,
      variants: {
        showRsf: this.showRsf,
        shouldShowClientFacingNames: this.shouldShowClientFacingNames,
        isOpen: this.isOpen,
      },
    };

    switch (this.exportableType) {
      case ProgramSummaryExportableTypes.ONE_PAGER: {
        const reportEntries = [
          {
            ...reportEntry,
            variants:
              this.scenario.isDetailed || this.worksheet.hasStorageRows
                ? {
                    ...reportEntry.variants,
                    isOpen: false,
                  }
                : reportEntry.variants,
            screenshotsOpts: [
              { width: 1440, format: this.format.toUpperCase() },
            ],
          },
        ];

        if (this.scenario.isDetailed || this.worksheet.hasStorageRows) {
          reportEntries.push({
            ...reportEntry,
            variants: {
              ...reportEntry.variants,
              isOpen: true,
            },
            screenshotsOpts: [
              { width: 1440, format: this.format.toUpperCase() },
            ],
          });
        }

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries,
        };
      }
      case ProgramSummaryExportableTypes.ALL_CARDS_INDIVIDUALLY: {
        const programSummaryCards = this.getProgramSummaryCards();

        const categories = Object.entries(this.worksheet.rowsByCategory)
          .filter(([category, rows]) => {
            return (
              ![CUSTOM_CATEGORY, INDIVIDUAL_WORKSPACES_CATEGORY].includes(
                category
              ) && rows.length > 0
            );
          })
          .map(([category, _]) => category);

        const individualWorkspacesCards = ['usfMix', 'seatMix'];
        if (isMacro(this.scenario)) {
          individualWorkspacesCards.push('seatMixSubtotals');
        }

        const categoryScreenshotOptions = individualWorkspacesCards
          .map((cardType) => {
            const width = this.getIndividualWorkspacesCardWidth(cardType);

            return categoryCardScreenshotOptions(
              this.format.toUpperCase(),
              INDIVIDUAL_WORKSPACES_CATEGORY,
              cardType,
              width
            );
          })
          .concat(
            categories.reduce((opts, category) => {
              const categoryOpts = ['usfMix', 'ratios'].map((cardType) => {
                const width = isMacro(this.scenario)
                  ? MACRO_PROGRAM_SUMMARY_CARD_WIDTH[cardType]
                  : PROGRAM_SUMMARY_CARD_WIDTH[cardType];

                return categoryCardScreenshotOptions(
                  this.format.toUpperCase(),
                  category,
                  cardType,
                  width
                );
              });

              return opts.concat(categoryOpts);
            }, [])
          );

        const reportEntries = [
          {
            ...reportEntry,
            variants: this.scenario.isDetailed
              ? {
                  ...reportEntry.variants,
                  isOpen: false,
                }
              : reportEntry.variants,
            screenshotsOpts: programSummaryCards
              .map(
                (programSummaryCard: string) =>
                  ({
                    format: this.format.toUpperCase(),
                    exportable: programSummaryCard,
                    width: 1440,
                    clip: {
                      x: 0,
                      y: 0,
                      width: isMacro(this.scenario)
                        ? MACRO_PROGRAM_SUMMARY_CARD_WIDTH[programSummaryCard]
                        : PROGRAM_SUMMARY_CARD_WIDTH[programSummaryCard],
                    },
                    pageActions:
                      PROGRAM_SUMMARY_CARD_ACTIONS[programSummaryCard],
                  } as ScreenshotOpts)
              )
              .concat(categoryScreenshotOptions),
          },
        ];

        if (this.scenario.isDetailed) {
          reportEntries.push({
            ...reportEntry,
            variants: {
              ...reportEntry.variants,
              isOpen: true,
            },
            screenshotsOpts: categoryScreenshotOptions.filter(
              (screenshotOption) =>
                screenshotOption.exportable.startsWith(
                  INDIVIDUAL_WORKSPACES_CATEGORY
                ) ||
                screenshotOption.exportable.startsWith(SUPPORT_SPACES_CATEGORY)
            ),
          });
        }

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries,
        };
      }
      case ProgramSummaryExportableTypes.SUMMARY_DATA: {
        const programSummaryCards = this.getProgramSummaryCards();

        programSummaryCards.push('programSummaryReport');

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: [
            {
              ...reportEntry,
              screenshotsOpts: programSummaryCards.map(
                (programSummaryCard: string) =>
                  ({
                    format: this.format.toUpperCase(),
                    exportable: programSummaryCard,
                    width: 1440,
                    clip: {
                      x: 0,
                      y: 0,
                      width: isMacro(this.scenario)
                        ? MACRO_PROGRAM_SUMMARY_CARD_WIDTH[programSummaryCard]
                        : PROGRAM_SUMMARY_CARD_WIDTH[programSummaryCard],
                    },
                    pageActions:
                      PROGRAM_SUMMARY_CARD_ACTIONS[programSummaryCard],
                  } as ScreenshotOpts)
              ),
            },
          ],
        };
      }

      case `${INDIVIDUAL_WORKSPACES_CATEGORY}Cards`: {
        let cards = ['usfMix', 'seatMix'];
        if (isMacro(this.scenario)) {
          cards = cards.concat([
            'seatMixSubtotals',
            'usfMixSeatMixAndSubtotals',
          ]);
        } else {
          cards.push('usfAndSeatMix');
        }

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: this.getCategoryReportEntries(
            reportEntry,
            INDIVIDUAL_WORKSPACES_CATEGORY,
            cards
          ),
        };
      }

      case `${MEETING_SPACES_CATEGORY}Cards`:
      case `${AMENITY_SPACES_CATEGORY}Cards`:
      case `${SUPPORT_SPACES_CATEGORY}Cards`: {
        const category = this.exportableType.replace('Cards', '');
        const cards = ['usfMix', 'ratios', 'usfMixAndRatios'];

        return {
          projectId: this.projectId,
          downloadAsZip: true,
          reportEntries: this.getCategoryReportEntries(
            reportEntry,
            category,
            cards
          ),
        };
      }

      default:
        return {
          projectId: this.projectId,
          reportEntries: [],
        } as ReportRequest;
    }
  }
}
